import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api, type DemoTrailerLoaderJobResponse } from '../lib/api'
import { useAuth } from '../lib/auth-context'

const ALLOWED_ACCOUNT_TYPES = new Set(['SUPER_ADMIN', 'AUX_ADMIN', 'ADMIN', 'CARRIER_ADMIN', 'DISPATCHER'])

export default function TrailerLoaderDemoCard({ className = '' }: { className?: string }) {
  const { user } = useAuth()
  const accountType = user?.accountType ?? ''
  const [result, setResult] = useState<DemoTrailerLoaderJobResponse | null>(null)

  const demoMut = useMutation({
    mutationFn: () => api.jobs.createTrailerLoaderDemo().then(r => r.data),
    onSuccess: response => setResult(response),
  })

  if (!user || !ALLOWED_ACCOUNT_TYPES.has(accountType)) {
    return null
  }

  return (
    <div className={`rounded-2xl border border-green-200 bg-green-50/70 p-5 shadow-sm ${className}`.trim()}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-green-900">Trailer Loader Beta</h2>
          <p className="mt-1 text-sm text-green-800">Preview AI-assisted trailer planning and driver-safe load layouts.</p>
          <p className="mt-1 text-xs text-green-700 italic">Advanced Preview — Beta feature</p>
        </div>
        {result && (
          <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
            {result.status}
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => demoMut.mutate()}
          disabled={demoMut.isPending}
          className="rounded-lg bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {demoMut.isPending ? 'Working…' : 'Create/Open Demo Loader Job'}
        </button>

        {result && (
          <>
            <Link to={result.links.driverLoaderPreview} className="rounded-lg border border-green-300 bg-white px-4 py-2 text-sm font-semibold text-green-800 hover:bg-green-100">
              Preview Trailer Plan
            </Link>
            <Link to={result.links.adminTrailerPlanner} className="rounded-lg border border-green-300 bg-white px-4 py-2 text-sm font-semibold text-green-800 hover:bg-green-100">
              Open Advanced Trailer Planning
            </Link>
            <Link to={result.links.driverJobDetail} className="rounded-lg border border-green-300 bg-white px-4 py-2 text-sm font-semibold text-green-800 hover:bg-green-100">
              Open Driver Job Detail
            </Link>
          </>
        )}
      </div>

      {demoMut.isError && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(demoMut.error as any)?.response?.data?.error ?? (demoMut.error as Error).message}
        </div>
      )}

      {result && (
        <div className="mt-4 rounded-xl border border-green-200 bg-white p-4 text-sm text-gray-700">
          <div className="font-medium text-gray-900">{result.driverName}</div>
          <div className="mt-1 text-xs text-gray-500">Load {result.loadId} · Job {result.jobId} · {result.equipmentComboKey}</div>
        </div>
      )}
    </div>
  )
}